
import { defineComponent, ref, onMounted, onActivated } from "vue";
import { MBDatatable } from "magicbean-saas-common";
import FilterDropdown from "@/views/documents/proforma-invoice/FilterDropdown.vue";
import { MenuComponent } from "@/assets/ts/components";
import { ApiDocumentsInvoice } from "@/core/api";
import _ from "lodash";
import mixin from "@/mixins";
import { useI18n } from "vue-i18n";
import { FilterOption, SortOption } from "@/core/directive/interface/common";
import { ProformaInvoiceTable } from "@/core/directive/interface/proformaInvocie";
import store from "@/store";
import {
  commonChangeFilterAddDate,
  commonExportFile,
  commonPreviewFile,
  formatDateTime,
  setModuleBCN,
} from "@/core/directive/function/common";
import { useRoute } from "vue-router";
import { Mutations } from "@/store/enums/StoreEnums";

export default defineComponent({
  name: "documents-proforma-invoice",
  components: {
    MBDatatable,
    FilterDropdown,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();

    const {
      showValidateErrorMsg,
      showFormSubmitSuccessMsg,
      showServerErrorMsg,
    } = mixin();

    const loading = ref(true);
    const tableData = ref<Array<ProformaInvoiceTable>>([]);
    const checkedCompanys = ref([]);
    const currentPage = ref<number>(1);
    const total = ref<number>(1);
    const pageSize = ref<number>(
      store.getters.currentConfigPageSize(route.name)
    );
    const search = ref<string>("");
    const filterOptions = ref<Array<FilterOption>>([]);
    const sortOptions = ref<Array<SortOption>>([
      { field: "invoice_number", direction: "desc" },
    ]);
    const downloadDisabled = ref<boolean>(false);
    let childWin: any;

    const tableHeader = ref([
      {
        name: t("proformaInvoice.proformaInvoiceNo"),
        key: "invoice_number",
        sortable: false,
      },
      {
        name: t("proformaInvoice.dealerNo"),
        key: "dealer_number",
        sortable: false,
      },
      {
        name: t("proformaInvoice.vatId"),
        key: "vat_id",
        sortable: false,
      },
      {
        name: t("proformaInvoice.subTotalAmount"),
        key: "sub_total_amount",
        sortable: false,
        align: "right",
      },
      {
        name: t("proformaInvoice.totalGrossAmount"),
        key: "gross_total_amount",
        sortable: false,
        align: "right",
      },
      {
        name: t("proformaInvoice.creationDate"),
        key: "created_at",
        sortable: true,
      },
      {
        name: t("proformaInvoice.trackNumber"),
        key: "track_number",
        sortable: false,
      },
      {
        name: t("common.status"),
        key: "status",
        sortable: false,
      },
      {
        name: t("common.action"),
        key: "actions",
        sortable: false,
      },
    ]);

    const init = () => {
      MenuComponent.reinitialization();
      setModuleBCN(t, route);
      getInvoiceList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    onMounted(() => {
      if (store.getters.getAliveList.length === 0) {
        init();
      }
    });

    onActivated(() => {
      init();
    });

    const getInvoiceList = (
      page: number,
      pageSize: number,
      search?: string,
      filterOptions?: Array<FilterOption>,
      sortOptions?: Array<SortOption>
    ) => {
      loading.value = true;
      ApiDocumentsInvoice.getInvoiceList({
        page: page,
        page_size: pageSize,
        search_value: search,
        filter_group: filterOptions,
        sort_orders: sortOptions,
      })
        .then(({ data }) => {
          loading.value = false;
          if (data.code == 0) {
            tableData.value.splice(
              0,
              tableData.value.length,
              ...data.data.items
            );
            tableData.value.forEach((item) => {
              item.downloadDisabled = false;
              item.showDisabled = false;
            });
            total.value = data.data.total;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const updateList = () => {
      getInvoiceList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const onCurrentPageChange = (page: number) => {
      currentPage.value = page;
      getInvoiceList(
        page,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const onRowsPerPageChange = (pagesize: number) => {
      pageSize.value = pagesize;
      updateList();
      store.commit(Mutations.SET_CURRENT_PAGE_CONFIG, {
        key: route.name,
        data: {
          pageSize: pageSize.value,
        },
      });
    };

    const onColumnSort = (val) => {
      let sortObj: SortOption = {
        field: val.columnName,
        direction: val.order,
      };
      sortOptions.value.splice(0, sortOptions.value.length, sortObj);
      getInvoiceList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const handleFilter = (val) => {
      let filterArr: Array<FilterOption> = [];
      for (let item in val) {
        if (val[item] != "" && val[item] != null) {
          if (item == "created_at_start") {
            filterArr.push({
              field: "created_at",
              value: commonChangeFilterAddDate(val[item], 1),
              condition: "gteq",
            });
          } else if (item == "created_at_end") {
            filterArr.push({
              field: "created_at",
              value: commonChangeFilterAddDate(val[item]),
              condition: "lteq",
            });
          } else if (item == "shipment_id" || item == "sale_order_number") {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "eq",
            });
          } else {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "like",
            });
          }
        }
      }
      filterOptions.value.splice(0, filterOptions.value.length, ...filterArr);
      search.value = "";
      currentPage.value = 1;
      getInvoiceList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const handleFilterReset = () => {
      resetFilter();
      getInvoiceList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const resetFilter = () => {
      filterOptions.value.splice(0, filterOptions.value.length);
    };

    const debounceSearch = _.debounce(getInvoiceList, 1000);

    const searchItems = () => {
      currentPage.value = 1;
      debounceSearch(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const removeSearch = () => {
      search.value = "";
      currentPage.value = 1;
      updateList();
    };

    const statusClass = (status) => {
      let statusClass = "";
      switch (status) {
        case "New":
        case "Sell On Credit":
          statusClass = "badge-light-primary";
          break;
        case "Processing":
        case "Pending":
          statusClass = "badge-light-warning";
          break;
        case "Partial Shipped":
        case "Shipped":
          statusClass = "badge-light-info";
          break;
        case "Complete":
        case "Paid":
          statusClass = "badge-light-success";
          break;
        case "Refunded":
          statusClass = "badge-light-danger";
          break;
        case "Canceled":
          statusClass = "badge-light-dark";
          break;
        default:
          statusClass = "badge-light-primary";
      }
      return statusClass;
    };

    const fileExport = async (id, item) => {
      item.downloadDisabled = true;
      const data = await ApiDocumentsInvoice.printProformaInvoice({
        id: id,
        type: "proforma",
      });
      item.downloadDisabled = false;
      commonExportFile(data);
    };

    const fileShow = async (id, item) => {
      item.showDisabled = true;
      try {
        const { data } = await ApiDocumentsInvoice.previewProformaInvoice({
          id: id,
          type: "proforma",
        });
        item.showDisabled = false;
        if (data) {
          if (data.code === undefined) {
            commonPreviewFile(childWin, item.packing_list_number, data);
          } else {
            showServerErrorMsg(data);
          }
        }
      } catch (error) {
        item.showDisabled = false;
      }
    };

    return {
      t,
      formatDateTime,
      loading,
      tableHeader,
      tableData,
      currentPage,
      total,
      pageSize,
      search,
      checkedCompanys,
      downloadDisabled,
      getInvoiceList,
      onCurrentPageChange,
      onRowsPerPageChange,
      onColumnSort,
      searchItems,
      removeSearch,
      handleFilter,
      handleFilterReset,
      updateList,
      statusClass,
      fileExport,
      fileShow,
    };
  },
});
